/**
 * NewAppointmentModal
 * Feature: 005-clinic-scheduling
 *
 * Modal to create an appointment from a calendar slot click.
 * Mirrors ScheduleFormStep: tabs "Recorrente" and "Avulso" with the same
 * forms, overlap logic, and professional multi-select.
 *
 * Adds a patient search/selector at the top (not needed in ScheduleFormStep
 * because it already receives patientId from context).
 */

import { useState, useCallback, useEffect } from 'react'
import { format } from 'date-fns'
import { Search, X, CalendarPlus, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { useSchedulingProfessionals } from '../hooks/use-scheduling-professionals'
import {
  useCreateRecurringSchedule,
} from '../hooks/use-recurring-schedules'
import { useCreateOneOffAppointment } from '../hooks/use-appointments'
import { useOverlapPolicy } from '../hooks/use-overlap-policy'
import { useOverlapCheck } from '../hooks/use-overlap-check'
import { RecurringScheduleForm } from './RecurringScheduleForm'
import { OneOffAppointmentForm } from './OneOffAppointmentForm'
import { OverlapWarningDialog } from './OverlapWarningDialog'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type {
  CreateRecurringScheduleFormData,
  CreateOneOffAppointmentFormData,
  OverlapCheckResult,
  DayOfWeek,
} from '../types/scheduling.types'

interface PatientOption {
  id: string
  full_name: string
  birth_date: string
}

export interface NewAppointmentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDate?: string
  initialStartTime?: string
  initialEndTime?: string
  initialPatientId?: string
}

export function NewAppointmentModal({
  open,
  onOpenChange,
  initialDate = '',
  initialStartTime = '',
  initialEndTime = '',
  initialPatientId,
}: NewAppointmentModalProps) {
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  // Patient search state
  const [patientSearch, setPatientSearch] = useState('')
  const [patientResults, setPatientResults] = useState<PatientOption[]>([])
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null)
  const [isSearchingPatient, setIsSearchingPatient] = useState(false)

  // Tabs & overlap state — mirrors ScheduleFormStep exactly
  const [activeTab, setActiveTab] = useState('recurring')
  const [overlapResult, setOverlapResult] = useState<OverlapCheckResult | null>(null)
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null)

  // Hooks
  const { data: professionals } = useSchedulingProfessionals()
  const { data: policy } = useOverlapPolicy()
  const createRecurring = useCreateRecurringSchedule()
  const createOneOff = useCreateOneOffAppointment()
  const { checkOverlap } = useOverlapCheck()

  const professionalOptions = professionals ?? []

  // Reset on open + pre-select patient if initialPatientId provided
  useEffect(() => {
    if (open) {
      setSelectedPatient(null)
      setPatientSearch('')
      setPatientResults([])
      setActiveTab('recurring')
      setOverlapResult(null)
      setPendingSubmit(null)

      if (initialPatientId && currentOrganization?.id) {
        supabase
          .from('patients')
          .select('id, full_name, birth_date')
          .eq('id', initialPatientId)
          .single()
          .then(({ data }) => {
            if (data) {
              setSelectedPatient({
                id: data.id,
                full_name: data.full_name,
                birth_date: data.birth_date ?? '',
              })
            }
          })
      }
    }
  }, [open, initialPatientId, currentOrganization?.id])

  // Patient search with debounce
  useEffect(() => {
    if (patientSearch.length < 2) {
      setPatientResults([])
      return
    }
    const timer = setTimeout(async () => {
      if (!currentOrganization?.id) return
      setIsSearchingPatient(true)
      try {
        const { data } = await supabase
          .from('patients')
          .select('id, full_name, birth_date')
          .eq('organization_id', currentOrganization.id)
          .eq('status', 'active')
          .ilike('full_name', `%${patientSearch}%`)
          .order('full_name')
          .limit(15)
        setPatientResults(data ?? [])
      } finally {
        setIsSearchingPatient(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [patientSearch, currentOrganization?.id])

  // ── Overlap handler (same as ScheduleFormStep) ────────────────────────────
  const handleOverlapDetected = useCallback(
    (result: OverlapCheckResult) => {
      if (result.has_conflicts && result.policy === 'not_allowed') {
        toast({
          title: 'Conflito de horário',
          description:
            'Não é possível agendar neste horário pois há conflito com outro atendimento.',
          variant: 'destructive',
        })
      }
    },
    [toast]
  )

  // ── Recurring submit (mirrors ScheduleFormStep.handleRecurringSubmit) ─────
  const handleRecurringSubmit = async (data: CreateRecurringScheduleFormData) => {
    if (policy && policy !== 'allowed') {
      const today = new Date()
      const targetDay = Number(data.day_of_week)
      const daysUntil = (targetDay - today.getDay() + 7) % 7 || 7
      const nextDate = new Date(today)
      nextDate.setDate(today.getDate() + daysUntil)

      const result = await checkOverlap(
        {
          professional_ids: data.professional_ids,
          session_date: format(nextDate, 'yyyy-MM-dd'),
          start_time: data.start_time,
          end_time: data.end_time,
        },
        policy
      )

      if (result.has_conflicts) {
        if (policy === 'not_allowed') {
          toast({
            title: 'Conflito de horário',
            description:
              'Não é possível agendar neste horário. Altere o horário ou os profissionais.',
            variant: 'destructive',
          })
          return
        }
        if (policy === 'allowed_with_warning') {
          setOverlapResult(result)
          setPendingSubmit(() => async () => { await executeRecurringCreate(data) })
          return
        }
      }
    }
    await executeRecurringCreate(data)
  }

  const executeRecurringCreate = async (data: CreateRecurringScheduleFormData) => {
    const result = await createRecurring.mutateAsync(data)
    if (result.success) {
      toast({ title: 'Agendamento criado', description: 'O agendamento recorrente foi criado com sucesso.' })
      onOpenChange(false)
    } else {
      toast({ title: 'Erro', description: result.error ?? 'Erro ao criar agendamento.', variant: 'destructive' })
    }
  }

  // ── One-off submit (mirrors ScheduleFormStep.handleOneOffSubmit) ──────────
  const handleOneOffSubmit = async (data: CreateOneOffAppointmentFormData) => {
    if (policy && policy !== 'allowed') {
      const result = await checkOverlap(
        {
          professional_ids: data.professional_ids,
          session_date: data.session_date,
          start_time: data.start_time,
          end_time: data.end_time,
        },
        policy
      )

      if (result.has_conflicts) {
        if (policy === 'not_allowed') {
          toast({
            title: 'Conflito de horário',
            description:
              'Não é possível agendar neste horário. Altere o horário ou os profissionais.',
            variant: 'destructive',
          })
          return
        }
        if (policy === 'allowed_with_warning') {
          setOverlapResult(result)
          setPendingSubmit(() => async () => { await executeOneOffCreate(data) })
          return
        }
      }
    }
    await executeOneOffCreate(data)
  }

  const executeOneOffCreate = async (data: CreateOneOffAppointmentFormData) => {
    const result = await createOneOff.mutateAsync(data)
    if (result.success) {
      toast({ title: 'Atendimento agendado', description: 'O atendimento avulso foi agendado com sucesso.' })
      onOpenChange(false)
    } else {
      toast({ title: 'Erro', description: result.error ?? 'Erro ao agendar atendimento.', variant: 'destructive' })
    }
  }

  const handleOverlapConfirm = () => {
    pendingSubmit?.()
    setOverlapResult(null)
    setPendingSubmit(null)
  }

  const handleOverlapCancel = () => {
    setOverlapResult(null)
    setPendingSubmit(null)
  }

  // Day of week derived from the clicked date (for pre-filling recurring form)
  const initialDayOfWeek: DayOfWeek | undefined = initialDate
    ? (new Date(initialDate + 'T00:00:00').getDay() as DayOfWeek)
    : undefined

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" />
              Novo Agendamento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* ── Patient selector ──────────────────────────────────────── */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Paciente</p>

              {selectedPatient ? (
                <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/40">
                  <span className="flex-1 text-sm font-medium">{selectedPatient.full_name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setSelectedPatient(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar paciente pelo nome..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="pl-9"
                  />
                  {isSearchingPatient && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {patientResults.length > 0 && (
                    <div className="absolute z-[9999] mt-1 w-full rounded-md border bg-popover shadow-md">
                      {patientResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full flex flex-col px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          onClick={() => {
                            setSelectedPatient(p)
                            setPatientSearch('')
                            setPatientResults([])
                          }}
                        >
                          <span className="font-medium">{p.full_name}</span>
                          {p.birth_date && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(p.birth_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {patientSearch.length >= 2 && !isSearchingPatient && patientResults.length === 0 && (
                    <div className="absolute z-[9999] mt-1 w-full rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
                      Nenhum paciente encontrado.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Scheduling tabs (only shown after selecting a patient) ── */}
            {selectedPatient && (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="recurring">Recorrente</TabsTrigger>
                  <TabsTrigger value="one-off">Avulso</TabsTrigger>
                </TabsList>

                <TabsContent value="recurring">
                  <RecurringScheduleForm
                    patientId={selectedPatient.id}
                    professionals={professionalOptions}
                    onSubmit={handleRecurringSubmit}
                    onOverlapDetected={handleOverlapDetected}
                    isSubmitting={createRecurring.isPending}
                    defaultValues={
                      initialDayOfWeek !== undefined
                        ? { day_of_week: initialDayOfWeek }
                        : undefined
                    }
                  />
                </TabsContent>

                <TabsContent value="one-off">
                  <OneOffAppointmentForm
                    patientId={selectedPatient.id}
                    professionals={professionalOptions}
                    onSubmit={handleOneOffSubmit}
                    onOverlapDetected={handleOverlapDetected}
                    isSubmitting={createOneOff.isPending}
                    initialValues={{
                      session_date: initialDate,
                      start_time: initialStartTime,
                      end_time: initialEndTime,
                    }}
                  />
                </TabsContent>
              </Tabs>
            )}

            {/* Cancel button when no patient selected yet */}
            {!selectedPatient && (
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <OverlapWarningDialog
        open={!!overlapResult}
        onOpenChange={(open) => { if (!open) handleOverlapCancel() }}
        conflicts={overlapResult?.conflicts ?? []}
        onConfirm={handleOverlapConfirm}
        onCancel={handleOverlapCancel}
      />
    </>
  )
}
